import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AlertTooltip } from '@openx/components-core/lib/AlertTooltip/AlertTooltip.tsx';
import { type InventoryListByIdAPI, ListTypesAPI } from '@openx/types/inventoryList.ts';
import { AppInventoryVersion, type AppInventoryVersionType } from '@openx/types/targeting/appInventory.ts';
import {
  type AppBundleIdOptionPayload,
  type AppInventoryOptionPayload,
  InventoryContentOption,
} from '@openx/types/targeting/inventoryAndContent.ts';
import { AllowBlockType, ListIconType, type TargetingFieldsValues } from '@openx/types/targeting/targeting.ts';
import { ComparisonType } from '@openx/types/targeting/targetingValuesTypes.ts';
import { InfoIcon } from '@phosphor-icons/react/Info';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnyAction } from 'redux';
import { useTargetingDisabledState } from '../../../utils/hooks/useTargetingDisabledState.ts';
import { getAppInventoryV2PreviewData } from '../../AppInventory/components/AppInventoryFormPreviewItems/utils/getAppInventoryV2PreviewData.ts';
import { mapTypeToIconType } from '../../constants.ts';
import { AllowBlockToggle } from '../../shared/AllowBlockToggle/AllowBlockToggle.tsx';
import { DimensionChipLine } from '../../shared/DimensionChip/DimensionChipLine.tsx';
import { FiltersTitle } from '../../shared/FiltersTitle/FiltersTitle.tsx';
import { useInventoryAndContentLabels } from '../../shared/InventoryAndContent/constants.ts';
import { useAppInventoryInventoryListAllowed } from '../../shared/InventoryAndContent/featureFlags.ts';
import { resetFilter, setComparisonOpValue } from '../../shared/InventoryAndContent/inventoryHelpers.ts';
import { getAppGroupDisplayLabel } from '../../shared/InventoryAndContent/utils.ts';
import { InventoryListItems } from '../../shared/InventoryListItems/InventoryListItems.tsx';
import { SelectedItems } from '../../shared/SelectedItems/SelectedItems.tsx';
import type {
  SetComparisonTypeDispatch,
  SetInventoryListItemsDispatch,
} from '../InventoryAndContentTopBox/constants.ts';
import { useBlockingConfirmation } from '../InventoryAndContentTopBox/hooks/useBlockingConfirmation.tsx';
import { deriveBundleIdComparisonType } from './utils.ts';

const sectionHeaderSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  mb: 1,
};

const sectionLabelSx: SxProps<Theme> = {
  fontWeight: 'fontWeightBold',
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  color: 'text.secondary',
  letterSpacing: '0.5px',
};

const followingTextSx: SxProps<Theme> = {
  fontSize: '0.875rem',
  color: 'text.secondary',
};

type AppInventoryItemsSectionProps = {
  appInventoryContent: AppInventoryOptionPayload;
  appBundleIdContent: AppBundleIdOptionPayload;
  dispatchInventory: (action: AnyAction) => void;
  readonly?: boolean;
  appNameComparisonType: ComparisonType;
  setAppNameComparisonType: SetComparisonTypeDispatch;
  bundleIdComparisonType: ComparisonType;
  setBundleIdComparisonType: SetComparisonTypeDispatch;
  isNotLastItem?: boolean;
  switchState?: boolean;
  showDimensionChipLine?: boolean;
  accountUid: string;
  inventoryListItems?: InventoryListByIdAPI['values'];
  setInventoryListItems: SetInventoryListItemsDispatch;
  inventoryVersion?: AppInventoryVersionType;
  field?: TargetingFieldsValues;
};

export const AppInventoryItemsSection = ({
  appInventoryContent,
  appBundleIdContent,
  dispatchInventory,
  readonly = false,
  appNameComparisonType,
  setAppNameComparisonType,
  bundleIdComparisonType,
  setBundleIdComparisonType,
  isNotLastItem = false,
  switchState: chipLineSwitchState,
  showDimensionChipLine = true,
  accountUid,
  inventoryListItems,
  setInventoryListItems,
  inventoryVersion,
  field,
}: AppInventoryItemsSectionProps) => {
  const { t } = useTranslation();
  const labels = useInventoryAndContentLabels();
  const { showBlockingConfirmation } = useBlockingConfirmation();
  const isInventoryListAllowed = useAppInventoryInventoryListAllowed();

  const appBundleIdField = field ? `${field}.${InventoryContentOption.APP_BUNDLE_ID}` : undefined;
  const appGroupField = field ? `${field}.${InventoryContentOption.APP_GROUP}` : undefined;

  const { isDisabledOrHasError: isAppBundleIdDisabledOrHasError, errorReasonMessage: appBundleIdErrorReasonMessage } =
    useTargetingDisabledState(appBundleIdField);

  const { isDisabledOrHasError: isAppGroupDisabledOrHasError, errorReasonMessage: appGroupErrorReasonMessage } =
    useTargetingDisabledState(appGroupField);

  const { errorReasonMessage: sectionErrorReasonMessage } = useTargetingDisabledState(field);

  const inventoryListId = appBundleIdContent.inventorylist_id;
  const inventoryListCount = inventoryListItems?.length ?? 0;

  const { appNameEntries, bundleIdEntries, appNameCount, bundleIdCount, hasAppNameItems, hasBundleIdItems } = useMemo(
    () => getAppInventoryV2PreviewData({ appInventoryContent, appBundleIdContent }),
    [appInventoryContent, appBundleIdContent]
  );

  const totalCount = appNameCount + bundleIdCount + inventoryListCount;
  const hasAnyItems = hasAppNameItems || hasBundleIdItems || !!inventoryListId;

  const handleClearAll = useCallback(() => {
    dispatchInventory(resetFilter({ contentOption: InventoryContentOption.APP_GROUP }));
    dispatchInventory(resetFilter({ contentOption: InventoryContentOption.APP_BUNDLE_ID }));
    setInventoryListItems(undefined);
    setAppNameComparisonType(ComparisonType.EQUALS);
    setBundleIdComparisonType(ComparisonType.EQUALS);
  }, [dispatchInventory, setInventoryListItems, setAppNameComparisonType, setBundleIdComparisonType]);

  const handleClearAppName = useCallback(() => {
    dispatchInventory(resetFilter({ contentOption: InventoryContentOption.APP_GROUP }));
    setAppNameComparisonType(ComparisonType.EQUALS);
  }, [dispatchInventory, setAppNameComparisonType]);

  const handleClearBundleId = useCallback(() => {
    dispatchInventory(resetFilter({ contentOption: InventoryContentOption.APP_BUNDLE_ID }));
    // Keep App Name's mode - clearing must not silently flip Block to Allow
    setBundleIdComparisonType(deriveBundleIdComparisonType(undefined, appInventoryContent?.val));
  }, [dispatchInventory, setBundleIdComparisonType, appInventoryContent]);

  const handleAppNameChange = useCallback(
    (values: string[], comparisonType: ComparisonType) => {
      dispatchInventory(
        setComparisonOpValue[InventoryContentOption.APP_GROUP]({
          comparisonType,
          values,
        })
      );
      if (values.length === 0) {
        setAppNameComparisonType(ComparisonType.EQUALS);
        if (!hasBundleIdItems) {
          setBundleIdComparisonType(ComparisonType.EQUALS);
        }
      }
    },
    [dispatchInventory, setAppNameComparisonType, setBundleIdComparisonType, hasBundleIdItems]
  );

  const handleBundleIdChange = useCallback(
    (values: string[], comparisonType: ComparisonType) => {
      dispatchInventory(
        setComparisonOpValue[InventoryContentOption.APP_BUNDLE_ID]({
          comparisonType,
          values,
        })
      );
      if (values.length === 0 && appNameComparisonType !== ComparisonType.DOES_NOT_EQUAL) {
        setBundleIdComparisonType(ComparisonType.EQUALS);
      }
    },
    [dispatchInventory, appNameComparisonType, setBundleIdComparisonType]
  );

  const applyAppNameToggle = useCallback(
    (value: AllowBlockType) => {
      const newComparisonType = value === AllowBlockType.ALLOW ? ComparisonType.EQUALS : ComparisonType.DOES_NOT_EQUAL;
      const oldComparisonType = value === AllowBlockType.ALLOW ? ComparisonType.DOES_NOT_EQUAL : ComparisonType.EQUALS;

      const currentValues = appInventoryContent?.val?.[oldComparisonType] ?? [];

      if (currentValues.length > 0) {
        dispatchInventory(
          setComparisonOpValue[InventoryContentOption.APP_GROUP]({
            comparisonType: newComparisonType,
            values: currentValues,
          })
        );
        dispatchInventory(
          setComparisonOpValue[InventoryContentOption.APP_GROUP]({
            comparisonType: oldComparisonType,
            values: [],
          })
        );
      }

      setAppNameComparisonType(newComparisonType);
    },
    [setAppNameComparisonType, appInventoryContent?.val, dispatchInventory]
  );

  const applyBundleIdToggle = useCallback(
    (value: AllowBlockType) => {
      const newComparisonType = value === AllowBlockType.ALLOW ? ComparisonType.EQUALS : ComparisonType.DOES_NOT_EQUAL;
      const oldComparisonType = value === AllowBlockType.ALLOW ? ComparisonType.DOES_NOT_EQUAL : ComparisonType.EQUALS;

      const currentValues = appBundleIdContent?.val?.[oldComparisonType] ?? [];

      if (currentValues.length > 0) {
        dispatchInventory(
          setComparisonOpValue[InventoryContentOption.APP_BUNDLE_ID]({
            comparisonType: newComparisonType,
            values: currentValues,
          })
        );
        dispatchInventory(
          setComparisonOpValue[InventoryContentOption.APP_BUNDLE_ID]({
            comparisonType: oldComparisonType,
            values: [],
          })
        );
      }

      setBundleIdComparisonType(newComparisonType);
    },
    [setBundleIdComparisonType, appBundleIdContent?.val, dispatchInventory]
  );

  const handleAppNameToggle = useCallback(
    (value: AllowBlockType) => {
      if (isAppGroupDisabledOrHasError) return;

      if ((inventoryListId || hasBundleIdItems) && value === AllowBlockType.BLOCK) {
        showBlockingConfirmation({
          hasManualBundles: hasBundleIdItems,
          hasInventoryList: !!inventoryListId,
          onConfirmed: () => {
            applyAppNameToggle(value);
            applyBundleIdToggle(AllowBlockType.BLOCK);
            if (inventoryListId) {
              dispatchInventory(
                setComparisonOpValue[InventoryContentOption.APP_BUNDLE_ID]({
                  comparisonType: ComparisonType.DOES_NOT_EQUAL,
                  inventorylist_id: null,
                })
              );
            }
          },
        });
        return;
      }
      applyAppNameToggle(value);
      if (value === AllowBlockType.BLOCK) {
        setBundleIdComparisonType(ComparisonType.DOES_NOT_EQUAL);
      } else if (!hasBundleIdItems) {
        setBundleIdComparisonType(ComparisonType.EQUALS);
      }
    },
    [
      isAppGroupDisabledOrHasError,
      applyAppNameToggle,
      applyBundleIdToggle,
      inventoryListId,
      hasBundleIdItems,
      showBlockingConfirmation,
      dispatchInventory,
      setBundleIdComparisonType,
    ]
  );

  const handleBundleIdToggle = useCallback(
    (value: AllowBlockType) => {
      if (isAppBundleIdDisabledOrHasError) return;

      // If Inventory List is active and switching to BLOCK, show confirmation first
      if (inventoryListId && value === AllowBlockType.BLOCK) {
        showBlockingConfirmation({
          onConfirmed: () => {
            applyBundleIdToggle(value);
            // Remove inventory list — it is restricted to 'Allow' only
            dispatchInventory(
              setComparisonOpValue[InventoryContentOption.APP_BUNDLE_ID]({
                comparisonType: ComparisonType.DOES_NOT_EQUAL,
                inventorylist_id: null,
              })
            );
          },
        });
        return;
      }
      applyBundleIdToggle(value);
    },
    [isAppBundleIdDisabledOrHasError, applyBundleIdToggle, inventoryListId, showBlockingConfirmation, dispatchInventory]
  );

  const handleRemoveInventoryList = useCallback(() => {
    if (isAppBundleIdDisabledOrHasError) return;

    dispatchInventory(
      setComparisonOpValue[InventoryContentOption.APP_BUNDLE_ID]({
        comparisonType: bundleIdComparisonType,
        inventorylist_id: null,
      })
    );
  }, [dispatchInventory, bundleIdComparisonType, isAppBundleIdDisabledOrHasError]);

  const appNameAllowBlock = useMemo(
    () => (appNameComparisonType === ComparisonType.EQUALS ? AllowBlockType.ALLOW : AllowBlockType.BLOCK),
    [appNameComparisonType]
  );

  const bundleIdAllowBlock = useMemo(
    () => (bundleIdComparisonType === ComparisonType.EQUALS ? AllowBlockType.ALLOW : AllowBlockType.BLOCK),
    [bundleIdComparisonType]
  );

  const isAppInventoryV2Only = inventoryVersion === AppInventoryVersion.APP_INVENTORY_ONLY_V2;

  if (!hasAnyItems && readonly) {
    return null;
  }

  return (
    <Grid data-test="app-inventory-items-section">
      {!isAppInventoryV2Only && (
        <FiltersTitle
          onClear={handleClearAll}
          title={`${labels[InventoryContentOption.APP_GROUP].label} [ ${totalCount} ]`}
          readonly={readonly}
          disabledReasonMessage={sectionErrorReasonMessage}
        />
      )}

      {hasAppNameItems && (
        <Box sx={{ mb: 2 }} data-test="app-name-items-section">
          {isAppInventoryV2Only ? (
            <FiltersTitle
              onClear={isAppGroupDisabledOrHasError ? undefined : handleClearAppName}
              title={`${t('App Name')} [ ${appNameCount} ]`}
              readonly={readonly || isAppGroupDisabledOrHasError}
              disabledReasonMessage={appGroupErrorReasonMessage}
            />
          ) : (
            <Typography sx={sectionLabelSx}>{t('App Name')}</Typography>
          )}

          {!readonly && (
            <Box sx={sectionHeaderSx}>
              <AllowBlockToggle
                value={appNameAllowBlock}
                onChange={handleAppNameToggle}
                disabled={isAppGroupDisabledOrHasError}
                dataTest="app-name-toggle"
              />
              <Typography sx={followingTextSx}>{t('of the following:')}</Typography>
            </Box>
          )}

          {appNameEntries.map(
            ([comparisonType, items]) =>
              items?.length > 0 && (
                <Grid
                  key={`app-name-${comparisonType}`}
                  data-test={`app-name-items-${comparisonType === ComparisonType.EQUALS ? 'allow' : 'block'}`}
                >
                  <SelectedItems
                    filtersTitle={comparisonType === ComparisonType.EQUALS ? t('Allowed') : t('Blocked')}
                    isAddIcon={mapTypeToIconType[comparisonType] === ListIconType.INCLUDE}
                    selectedItems={items}
                    getItemLabel={getAppGroupDisplayLabel}
                    readonly={readonly || isAppGroupDisabledOrHasError}
                    handleChange={values => handleAppNameChange(values, comparisonType as ComparisonType)}
                    isRemovingAll={!readonly && !isAppGroupDisabledOrHasError}
                    dataTest="app-name-item"
                  />
                </Grid>
              )
          )}
        </Box>
      )}

      {hasBundleIdItems && (
        <Box sx={{ mb: 2 }} data-test="app-bundle-id-items-section">
          {isAppInventoryV2Only ? (
            <FiltersTitle
              onClear={isAppBundleIdDisabledOrHasError ? undefined : handleClearBundleId}
              title={`${inventoryListId ? t('App Bundle ID (Manually)') : t('App Bundle ID')} [ ${bundleIdCount} ]`}
              readonly={readonly || isAppBundleIdDisabledOrHasError}
              disabledReasonMessage={appBundleIdErrorReasonMessage}
            />
          ) : (
            <Typography sx={sectionLabelSx}>
              {inventoryListId ? t('App Bundle ID (Manually)') : t('App Bundle ID')}
            </Typography>
          )}

          {!readonly && (
            <Box sx={sectionHeaderSx}>
              <AllowBlockToggle
                value={bundleIdAllowBlock}
                onChange={handleBundleIdToggle}
                disableAllow={appNameComparisonType === ComparisonType.DOES_NOT_EQUAL}
                disabled={isAppBundleIdDisabledOrHasError}
                dataTest="app-bundle-id-toggle"
              />
              <Typography sx={followingTextSx}>{t('of the following:')}</Typography>
            </Box>
          )}

          {bundleIdEntries.map(
            ([comparisonType, items]) =>
              items?.length > 0 && (
                <Grid
                  key={`bundle-id-${comparisonType}`}
                  data-test={`bundle-id-items-${comparisonType === ComparisonType.EQUALS ? 'allow' : 'block'}`}
                >
                  <SelectedItems
                    filtersTitle={comparisonType === ComparisonType.EQUALS ? t('Allowed') : t('Blocked')}
                    isAddIcon={mapTypeToIconType[comparisonType] === ListIconType.INCLUDE}
                    selectedItems={items}
                    readonly={readonly || isAppBundleIdDisabledOrHasError}
                    handleChange={values => handleBundleIdChange(values, comparisonType as ComparisonType)}
                    isRemovingAll={!readonly && !isAppBundleIdDisabledOrHasError}
                    dataTest="bundle-id-item"
                  />
                </Grid>
              )
          )}
        </Box>
      )}

      {isInventoryListAllowed && inventoryListId && (
        <Box sx={{ mb: 2 }} data-test="inventory-list-items-from-list">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: isAppInventoryV2Only ? 2 : 0 }}>
            {isAppInventoryV2Only ? (
              <FiltersTitle
                onClear={isAppBundleIdDisabledOrHasError ? undefined : handleRemoveInventoryList}
                title={`${t('App Bundle ID (From the Inventory List)')} [ ${inventoryListCount} ]`}
                readonly={readonly || isAppBundleIdDisabledOrHasError}
                disabledReasonMessage={appBundleIdErrorReasonMessage}
                sx={{ mt: isAppInventoryV2Only ? 0 : 1 }}
              />
            ) : (
              <Typography sx={sectionLabelSx}>{t('App Bundle ID (From the Inventory List)')}</Typography>
            )}
            <AlertTooltip
              icon={<InfoIcon />}
              color="default"
              title={t("App Bundles from Inventory Lists are restricted to 'Allow' targeting only.")}
            />
          </Box>
          <InventoryListItems
            listType={ListTypesAPI.BUNDLE}
            readonly={readonly || isAppBundleIdDisabledOrHasError}
            accountUid={accountUid}
            inventorylistId={inventoryListId}
            setInventoryListItems={setInventoryListItems}
            onChange={handleRemoveInventoryList}
            hideTitle
            enableRemoveOneItem={!readonly && !isAppBundleIdDisabledOrHasError}
          />
        </Box>
      )}

      {isNotLastItem && showDimensionChipLine && <DimensionChipLine switchState={chipLineSwitchState} />}
    </Grid>
  );
};
